import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { heritageColors } from '../../../../heritageTheme.ts';
import { getIconById } from '../../../NitzavSpotlightWidget/iconOptions';
import {
  PARNAS_TYPE_LABELS,
  SLOT_TYPE_COLORS,
  HEBREW_MONTH_NAMES,
  HEB_DAY_NUMS,
  fetchHebDateLabel,
} from '../../parnasConstants';

/**
 * Spread — כרטיס מרכזי מלכותי עם כל הנציבים + שבוע קדימה ואחורה
 * העבר מלמעלה, הנוכחי במרכז (3 עמודות), העתיד מלמטה.
 */

const GOLD        = '#b8860b';
const HEBREW_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

function getOffsetDays(effectiveDateISO, startOffset, count) {
  const base = effectiveDateISO ? new Date(effectiveDateISO + 'T12:00:00') : new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(base);
    d.setDate(d.getDate() + startOffset + i);
    return { iso: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`, hebrewDay: HEBREW_DAYS[d.getDay()] };
  });
}

function getShabbatOfWeek(isoDate) {
  const d = new Date(isoDate + 'T12:00:00');
  const daysToSat = (6 - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + daysToSat);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// ─── Mini day tile ────────────────────────────────────────────────────────────

function DayTile({ iso, hebrewDay, hebLabel, dailySponsor, weeklySponsor, isPast }) {
  const hasAny = dailySponsor || weeklySponsor;

  return (
    <Box sx={{
      flex: '0 0 70px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      border: `1.5px solid ${hasAny
        ? (isPast ? '#c8b89a' : GOLD)
        : heritageColors.border}`,
      borderRadius: '6px',
      px: 0.5,
      py: 0.75,
      textAlign: 'center',
      gap: '2px',
      background: isPast
        ? (hasAny ? 'linear-gradient(to bottom,#f9f7f3,#f5f2ec)' : '#f5f5f3')
        : (hasAny ? 'linear-gradient(to bottom,#fffdf7,#fdf8e8)' : '#fafaf9'),
      boxShadow: (!isPast && hasAny) ? '0 2px 8px rgba(184,134,11,0.12)' : 'none',
      opacity: isPast ? 0.75 : 1,
      overflow: 'hidden',
    }}>
      {/* Day name */}
      <Typography sx={{ color: heritageColors.navy, fontSize: '0.6em', fontWeight: 700, lineHeight: 1.1 }}>
        {hebrewDay}
      </Typography>

      {/* Hebrew date */}
      <Typography sx={{ color: heritageColors.slate, fontSize: '0.52em', opacity: 0.7, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
        {hebLabel || '…'}
      </Typography>

      <Box sx={{ width: '55%', height: '1px', bgcolor: hasAny ? (isPast ? '#c8b89a' : GOLD) : heritageColors.border, opacity: 0.4, my: '2px' }} />

      {/* Daily sponsor */}
      {dailySponsor ? (
        <Typography sx={{ color: SLOT_TYPE_COLORS.daily, fontWeight: 700, fontSize: '0.58em', lineHeight: 1.2, wordBreak: 'break-word' }}>
          {dailySponsor.sponsorName}
        </Typography>
      ) : isPast ? (
        <Typography sx={{ color: heritageColors.slate, fontSize: '0.5em', opacity: 0.35, lineHeight: 1 }}>—</Typography>
      ) : (
        <Typography sx={{ color: heritageColors.slate, fontWeight: 400, fontSize: '0.5em', opacity: 0.45, lineHeight: 1.1 }}>
          פנוי
        </Typography>
      )}

      {/* Weekly sponsor — subtle, smaller */}
      {weeklySponsor && (
        <Typography sx={{
          color: SLOT_TYPE_COLORS.weekly, fontSize: '0.5em', lineHeight: 1.1,
          fontStyle: 'italic', wordBreak: 'break-word', opacity: 0.85,
        }}>
          {weeklySponsor.sponsorName}
        </Typography>
      )}
    </Box>
  );
}

// ─── Slot column inside main card ─────────────────────────────────────────────

const ALL_SLOT_DEFS = [
  { key: 'daily',   label: 'פרנס היום',   color: SLOT_TYPE_COLORS.daily   },
  { key: 'weekly',  label: 'פרנס השבוע',  color: SLOT_TYPE_COLORS.weekly  },
  { key: 'monthly', label: 'פרנס החודש',  color: SLOT_TYPE_COLORS.monthly },
];

const DEFAULT_INCLUDED = { daily: true, weekly: true, monthly: true };

function SlotColumn({ def, sponsor, isLast, placeholderName, currentParshaName, todayHDay, todayHMonth }) {
  const empty = !sponsor;

  const subLabel =
    def.key === 'daily'   && todayHDay && todayHMonth
      ? `${HEB_DAY_NUMS[todayHDay] || todayHDay} ${HEBREW_MONTH_NAMES[todayHMonth] || ''}`
    : def.key === 'weekly'  && currentParshaName
      ? currentParshaName
    : def.key === 'monthly' && todayHMonth
      ? HEBREW_MONTH_NAMES[todayHMonth] || null
    : null;

  return (
    <Box sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      textAlign: 'center',
      px: 1,
      py: 0.5,
      borderLeft: isLast ? 'none' : `1px solid rgba(184,134,11,0.18)`,
      gap: 0.5,
      overflow: 'hidden',
    }}>
      {/* Colored top bar */}
      <Box sx={{
        width: '32px', height: '3px', borderRadius: '2px',
        bgcolor: def.color, opacity: empty ? 0.3 : 0.85, mb: 0.25,
      }} />

      {/* Type label */}
      <Typography sx={{
        color:         def.color,
        fontWeight:    700,
        fontSize:      '0.58em',
        letterSpacing: '0.06em',
        opacity:       empty ? 0.5 : 1,
        lineHeight:    1.1,
      }}>
        {def.label}
      </Typography>

      {/* Sub-label (parsha etc.) — always reserves space for alignment */}
      <Typography sx={{
        color: heritageColors.slate, fontSize: '0.52em',
        opacity: 0.6, lineHeight: 1, mb: 0.25,
        visibility: subLabel ? 'visible' : 'hidden',
      }}>
        {subLabel || ' '}
      </Typography>

      {/* Thin divider */}
      <Box sx={{
        width: '40%', height: '1px',
        bgcolor: def.color, opacity: empty ? 0.15 : 0.35,
        my: 0.25,
      }} />

      {/* Sponsor name — large & prominent */}
      <Typography sx={{
        color:      empty ? heritageColors.slate : heritageColors.navy,
        fontWeight: empty ? 400 : 800,
        fontSize:   empty ? '0.7em' : '0.95em',
        lineHeight: 1.25,
        opacity:    empty ? 0.28 : 1,
        wordBreak:  'break-word',
      }}>
        {empty ? placeholderName : sponsor.sponsorName}
      </Typography>

      {/* Note */}
      {!empty && sponsor.note && (
        <Typography sx={{
          color:     heritageColors.slate,
          fontStyle: 'italic',
          fontSize:  '0.58em',
          opacity:   0.75,
          lineHeight: 1.2,
          mt: 0.25,
        }}>
          {sponsor.note}
        </Typography>
      )}

      {/* Encourage if empty */}
      {empty && (
        <Typography sx={{
          color: heritageColors.slate, fontSize: '0.52em',
          opacity: 0.22, lineHeight: 1.2, mt: 0.25,
        }}>
          ניתן לתרום
        </Typography>
      )}
    </Box>
  );
}

// ─── Row label (7 ימים אחורה / קדימה) ────────────────────────────────────────

function RowLabel({ text, align = 'start' }) {
  return (
    <Box sx={{
      px: 1.25, pt: 0.5, pb: 0.4, flexShrink: 0,
      display: 'flex', alignItems: 'center', gap: 1,
      flexDirection: align === 'end' ? 'row-reverse' : 'row',
    }}>
      <Typography sx={{
        color: heritageColors.slate, fontSize: '0.6em', fontWeight: 600,
        letterSpacing: '0.05em', whiteSpace: 'nowrap', opacity: 0.7,
      }}>
        {text}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: heritageColors.border }} />
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const Spread = ({
  activeSponsors        = [],
  parnasType            = 'maor',
  parnasTypeCustomLabel = '',
  parnasTypeIcon        = 'star',
  currentParshaName     = '',
  todayHebrewLabel      = '',
  title                 = '',
  showTitle             = false,
  sponsors              = [],
  effectiveDateISO      = '',
  placeholderName       = 'השם שלך כאן',
  todayHMonth           = null,
  todayHDay             = null,
  includedSlotTypes     = DEFAULT_INCLUDED,
}) => {
  const TypeIcon  = getIconById(parnasTypeIcon || 'star');
  const typeLabel = parnasType === 'acher' && parnasTypeCustomLabel
    ? parnasTypeCustomLabel
    : (PARNAS_TYPE_LABELS[parnasType] || '');

  const included   = { ...DEFAULT_INCLUDED, ...includedSlotTypes };
  const SLOT_DEFS  = ALL_SLOT_DEFS.filter(d => included[d.key]);

  const pastDays   = getOffsetDays(effectiveDateISO, -7, 7);
  const futureDays = getOffsetDays(effectiveDateISO,  1, 7);

  const dailySponsorByDate = useMemo(() => {
    const map = {};
    for (const sponsor of sponsors)
      for (const slot of (sponsor.slots || []))
        if (slot.type === 'daily' && slot.date)
          map[slot.date] = { sponsorName: sponsor.name, note: slot.note || '' };
    return map;
  }, [sponsors]);

  const weeklySponsorByDate = useMemo(() => {
    const map = {};
    for (const sponsor of sponsors)
      for (const slot of (sponsor.slots || []))
        if (slot.type === 'weekly' && slot.parshaShabbatDate)
          map[slot.parshaShabbatDate] = { sponsorName: sponsor.name, note: slot.note || '' };
    return map;
  }, [sponsors]);

  // Hebrew dates for the 14 surrounding days
  const [hebDates, setHebDates] = useState({});

  useEffect(() => {
    const dates = [...pastDays, ...futureDays].map(d => d.iso);
    if (!dates.length) return;
    let cancelled = false;
    Promise.all(
      dates.map(iso =>
        fetchHebDateLabel(iso).then(label => ({ iso, label }))
      )
    ).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(({ iso, label }) => { map[iso] = label; });
      setHebDates(map);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDateISO]);

  // Today's active sponsors by type
  const byType = {
    daily:   activeSponsors.find(s => s.slotType === 'daily')   || null,
    weekly:  activeSponsors.find(s => s.slotType === 'weekly')  || null,
    monthly: activeSponsors.find(s => s.slotType === 'monthly') || null,
  };

  return (
    <Box dir="rtl" sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box sx={{
        px: 2, pt: 1.5, pb: 1, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 0.75,
      }}>
        <TypeIcon sx={{ fontSize: 18, color: heritageColors.gold }} />
        <Typography variant="h6" sx={{ color: heritageColors.navy, fontWeight: 700 }}>
          {showTitle && title ? title : `פרנסי ${typeLabel}`}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: heritageColors.border, flexShrink: 0 }} />

      {/* Past 7 days */}
      <RowLabel text="7 ימים אחורה" align="end" />
      <Box sx={{ display: 'flex', gap: 0.5, px: 1.25, pb: 0.75, flexShrink: 0, overflowX: 'auto' }}>
        {pastDays.map(({ iso, hebrewDay }) => (
          <DayTile
            key={iso} iso={iso} hebrewDay={hebrewDay}
            hebLabel={hebDates[iso]}
            dailySponsor={dailySponsorByDate[iso] || null}
            weeklySponsor={weeklySponsorByDate[getShabbatOfWeek(iso)] || null}
            isPast={true}
          />
        ))}
      </Box>

      <Divider sx={{ borderColor: heritageColors.border, mx: 1.5, flexShrink: 0 }} />

      {/* Main today card — 3-column layout */}
      <Box sx={{ flex: 1, px: 1.5, py: 1, overflow: 'hidden', minHeight: 0 }}>
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: `2px solid ${GOLD}`,
          borderRadius: '10px',
          overflow: 'hidden',
          background: 'linear-gradient(160deg, #fffdf7 0%, #fef8e4 55%, #fdf1cc 100%)',
          boxShadow: '0 4px 22px rgba(184,134,11,0.22), 0 1px 4px rgba(0,0,0,0.06)',
          position: 'relative',
        }}>
          {/* Top gold stripe */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
            background: `linear-gradient(to left, transparent, ${GOLD} 25%, #d4a017 50%, ${GOLD} 75%, transparent)`,
          }} />

          {/* Card header */}
          <Box sx={{
            pt: 1.5, pb: 0.75, px: 2,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexShrink: 0,
          }}>
            <Typography sx={{ color: GOLD, fontWeight: 800, fontSize: '0.7em', letterSpacing: '0.08em' }}>
              נציבי היום
            </Typography>
            {todayHebrewLabel && (
              <Typography sx={{ color: heritageColors.slate, fontSize: '0.62em', opacity: 0.7 }}>
                {todayHebrewLabel}
              </Typography>
            )}
          </Box>

          <Box sx={{ width: '85%', height: '1px', bgcolor: GOLD, opacity: 0.2, mx: 'auto', flexShrink: 0 }} />

          {/* 3-column sponsor display */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            minHeight: 0,
            py: 0.5,
          }}>
            {SLOT_DEFS.map((def, i) => (
              <SlotColumn
                key={def.key}
                def={def}
                sponsor={byType[def.key]}
                isLast={i === SLOT_DEFS.length - 1}
                placeholderName={placeholderName}
                currentParshaName={currentParshaName}
                todayHDay={todayHDay}
                todayHMonth={todayHMonth}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: heritageColors.border, mx: 1.5, flexShrink: 0 }} />

      {/* Future 7 days */}
      <RowLabel text="7 ימים קדימה" align="start" />
      <Box sx={{ display: 'flex', gap: 0.5, px: 1.25, pb: 1.25, flexShrink: 0, overflowX: 'auto' }}>
        {futureDays.map(({ iso, hebrewDay }) => (
          <DayTile
            key={iso} iso={iso} hebrewDay={hebrewDay}
            hebLabel={hebDates[iso]}
            dailySponsor={dailySponsorByDate[iso] || null}
            weeklySponsor={weeklySponsorByDate[getShabbatOfWeek(iso)] || null}
            isPast={false}
          />
        ))}
      </Box>

    </Box>
  );
};

export default Spread;
